// NearMe — the app's "Know Before You Go" landing tab.
//
// For the user's area (ZIP or GPS), shows the everyday scenarios and the cheapest
// QUALIFIED hospital near them for each — qualified = has every cornerstone code
// + >=50% of supporting codes by cost (the accurate model). Data comes from the
// compact bundled `near_me.json` pack (loadStaticData → bundled .dat first, R2
// fallback); distance is computed client-side with haversine. No DB at runtime.
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { MapPin, Navigation, Search, ChevronRight, Loader2, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/contexts/ThemeContext";
import { useUserLocation } from "@/hooks/useUserLocation";
import { haversineDistance } from "@/lib/scenarioCoverageStore";
import { loadStaticData } from "@/lib/offlineData";
import { useBrand } from "@/config/brands";

const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

interface PackHospital { n: string; c: string; s: string; la: number; lo: number; }
interface PackRow { h: string; p: number; ss: number; }
interface PackScenario { id: string; name: string; icon: string; national_avg: number | null; rows: PackRow[]; }
interface NearMePack { hospitals: Record<string, PackHospital>; scenarios: PackScenario[]; }

const RADII = [25, 60, 100, 250];
const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
const fmtUSD = (n: number) => `$${Math.round(n).toLocaleString()}`;

export default function NearMe() {
  const brand = useBrand();
  const { t } = useTranslation("scenarios");
  const { isDark } = useTheme();
  const { location, isLocating, error, detectLocation, lookupZip } = useUserLocation();
  const [zip, setZip] = useState("");
  const [radius, setRadius] = useState(60);

  const T = isDark
    ? { bg: "#0a1628", card: "rgba(255,255,255,0.05)", border: "rgba(197,165,90,0.18)", text: "#f0ebe3", muted: "rgba(240,235,227,0.6)", red: "#e2576a", accent: "#C5A55A", good: "#6bc9a0" }
    : { bg: "#FAF9F6", card: "#ffffff", border: "rgba(0,40,104,0.10)", text: "#1B1B1B", muted: "rgba(27,27,27,0.6)", red: "#B22234", accent: "#002868", good: "#2b5e49" };

  const { data: pack, isLoading } = useQuery({
    queryKey: ["near-me-pack"],
    queryFn: () => loadStaticData<NearMePack>("near_me.json"),
    staleTime: Infinity,
  });

  // For each scenario: cheapest qualified hospital within radius (rows are price-asc,
  // so the first within radius is the cheapest), plus the nearest as a fallback hint.
  const results = useMemo(() => {
    if (!pack || !location) return [];
    return pack.scenarios.map((sc) => {
      let best: { price: number; dist: number; h: PackHospital; hid: string } | null = null;
      let nearest: { dist: number } | null = null;
      let inRadius = 0;
      for (const r of sc.rows) {
        const h = pack.hospitals[r.h];
        if (!h || h.la == null || h.lo == null) continue;
        const dist = haversineDistance(location.lat, location.lng, h.la, h.lo);
        if (!nearest || dist < nearest.dist) nearest = { dist };
        if (dist <= radius) {
          inRadius++;
          if (!best) best = { price: r.p, dist, h, hid: r.h }; // first within radius = cheapest
        }
      }
      return { sc, best, nearest, inRadius };
    });
  }, [pack, location, radius]);

  const located = !!location;

  return (
    <div className="min-h-screen pb-32" style={{ backgroundColor: T.bg, color: T.text, fontFamily: sans }}>
      <Helmet><title>Know Before You Go — Prices Near You | {brand.name}</title></Helmet>

      {/* Header */}
      <section style={{ paddingTop: 20, paddingBottom: 12 }}>
        <div className="max-w-[800px] mx-auto px-6">
          <div className="flex items-center gap-2" style={{ color: T.accent, fontSize: 12, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" }}>
            <ShieldCheck className="w-4 h-4" /> {t("nearme.kicker")}
          </div>
          <h1 style={{ fontFamily: serif, fontSize: "clamp(28px,6vw,40px)", fontWeight: 400, lineHeight: 1.1, marginTop: 6 }}>
            {t("nearme.title")}
          </h1>
          <p style={{ color: T.muted, fontSize: 15, lineHeight: 1.5, marginTop: 6 }}>
            {t("nearme.subtitle")}
          </p>
        </div>
      </section>

      {/* Location chooser */}
      <section className="max-w-[800px] mx-auto px-6" style={{ marginBottom: 12 }}>
        <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: 14 }}>
          {located ? (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
                <MapPin className="w-5 h-5 flex-shrink-0" style={{ color: T.red }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{location!.label}</div>
                  <div style={{ fontSize: 12, color: T.muted }}>{t("nearme.showingWithin", { radius })}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {RADII.map((r) => (
                  <button key={r} onClick={() => setRadius(r)}
                    style={{ padding: "5px 9px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer",
                      background: radius === r ? T.accent : "transparent", color: radius === r ? "#fff" : T.muted,
                      border: radius === r ? "none" : `1px solid ${T.border}` }}>
                    {r}mi
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <button onClick={detectLocation} disabled={isLocating}
                className="flex items-center justify-center gap-2"
                style={{ background: T.red, color: "#fff", border: "none", borderRadius: 10, padding: "12px", fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: sans }}>
                {isLocating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                {t("nearme.useMyLocation")}
              </button>
              <div className="flex items-center gap-2">
                <div style={{ flex: 1, height: 1, background: T.border }} /><span style={{ fontSize: 12, color: T.muted }}>{t("nearme.or")}</span><div style={{ flex: 1, height: 1, background: T.border }} />
              </div>
              <form onSubmit={(e) => { e.preventDefault(); if (/^\d{5}$/.test(zip)) lookupZip(zip); }} className="flex gap-2">
                <input inputMode="numeric" maxLength={5} value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
                  placeholder={t("nearme.zipPlaceholder")}
                  style={{ flex: 1, padding: "12px 14px", borderRadius: 10, border: `1px solid ${T.border}`, background: isDark ? "rgba(0,0,0,0.2)" : "#fff", color: T.text, fontSize: 16, outline: "none", fontFamily: sans }} />
                <button type="submit" style={{ background: T.accent, color: "#fff", border: "none", borderRadius: 10, padding: "0 18px", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
                  <Search className="w-4 h-4" />
                </button>
              </form>
              {error && <div style={{ fontSize: 13, color: T.red }}>{error}</div>}
            </div>
          )}
        </div>
      </section>

      {/* Results */}
      <section className="max-w-[800px] mx-auto px-6">
        {!located && (
          <div style={{ textAlign: "center", color: T.muted, padding: "40px 20px", fontSize: 15 }}>
            {t("nearme.emptyPrompt")}
          </div>
        )}
        {located && isLoading && (
          <div className="flex items-center justify-center gap-2" style={{ color: T.muted, padding: 40 }}>
            <Loader2 className="w-5 h-5 animate-spin" /> {t("nearme.loadingPrices")}
          </div>
        )}
        {/* loadStaticData resolves null (never throws) on fetch failure — without
            this branch the spinner above spun forever on CDN errors/offline. */}
        {located && !isLoading && !pack && (
          <div style={{ textAlign: "center", color: T.muted, padding: "40px 20px", fontSize: 15 }}>
            {t("nearme.loadError")}{' '}
            <button onClick={() => window.location.reload()}
              style={{ color: T.red, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: 0 }}>
              {t("nearme.tryAgain")}
            </button>
          </div>
        )}
        {located && pack && (
          <div className="flex flex-col gap-2.5">
            {results.map(({ sc, best, nearest, inRadius }) => (
              <Link key={sc.id} to={`/scenarios/${sc.id}/${slugify(sc.name)}`}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12,
                  background: T.card, border: `1px solid ${T.border}`, textDecoration: "none", color: T.text }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.25 }}>{sc.name}</div>
                  {best ? (
                    <div style={{ fontSize: 12, color: T.muted, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {best.h.n} · {best.h.c}, {best.h.s}
                      {inRadius > 1 && <span style={{ color: T.accent }}> · {t("nearme.moreCount", { count: inRadius - 1 })}</span>}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>
                      {t("nearme.noFullyPriced", { radius })}{nearest ? ` · ${t("nearme.nearestMi", { dist: nearest.dist.toFixed(0) })}` : ""}
                    </div>
                  )}
                </div>
                {best && (
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 16, color: T.good }}>{fmtUSD(best.price)}</div>
                    <div style={{ fontSize: 11, color: T.muted, fontWeight: 600 }}>{t("nearme.milesAway", { dist: best.dist.toFixed(0) })}</div>
                    {sc.national_avg && (
                      <div style={{ fontSize: 10, color: best.price < sc.national_avg ? T.good : T.red }}>
                        {best.price < sc.national_avg ? "↓" : "↑"} {t("nearme.vsAvg", { avg: fmtUSD(sc.national_avg) })}
                      </div>
                    )}
                  </div>
                )}
                <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: T.muted }} />
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
